import { useState } from 'react'
import { FormDialog } from '@/components/common'
import { Label } from '@/components/ui/label'
import { failureSentence, MIN_OVERRIDE_REASON } from '../types'
import type { CredentialContext, CredentialFailureView } from '../types'
import { DatePicker } from '@/components/ui/date-picker'

export interface OverrideDialogProps {
  failure: CredentialFailureView | null
  context: CredentialContext
  onClose: () => void
  onConfirm: (reason: string, expiresAt: string | null) => Promise<void>
  submitting?: boolean
  error?: string | null
}

/**
 * Recording why somebody is being let past a rule.
 *
 * <p>The override is written <em>before</em> the thing it permits, and the wording says so.
 * That order is not an implementation detail: the workflow engine's decision API has nowhere
 * to carry a reason, so an approver who means to proceed has to record it first — and having
 * one order everywhere means nobody has to remember which screen works which way.
 *
 * <p>The reason is the whole value of the record, so the dialog refuses a short one rather
 * than letting the server return a validation error after the fact. Ten characters is the
 * same floor the database enforces.
 */
export function OverrideDialog({
  failure,
  context,
  onClose,
  onConfirm,
  submitting,
  error,
}: OverrideDialogProps) {
  const [reason, setReason] = useState('')
  const [expiresAt, setExpiresAt] = useState('')

  const tooShort = reason.trim().length < MIN_OVERRIDE_REASON

  return (
    <FormDialog
      open={failure != null}
      onOpenChange={(open) => {
        if (!open) {
          setReason('')
          setExpiresAt('')
          onClose()
        }
      }}
      title="Override this rule"
      description={
        failure
          ? `${failure.documentTypeName} is ${failureSentence(failure)}. Recording this override lets the placement go ahead, and it is kept.`
          : undefined
      }
      submitLabel="Record the override"
      loading={submitting}
      error={error}
      onSubmit={() => {
        if (!tooShort) void onConfirm(reason.trim(), expiresAt || null)
      }}
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="override-reason">Why is this going ahead?</Label>
          <textarea
            id="override-reason"
            required
            rows={3}
            minLength={MIN_OVERRIDE_REASON}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Renewal receipt sighted; the new passport is with the typing centre."
            className="w-full rounded-[var(--radius-input)] border border-[var(--color-border)] bg-[var(--color-card)] p-2 text-sm"
          />
          <p className={tooShort && reason.length > 0 ? 'text-xs text-[var(--color-danger)]' : 'text-xs text-[var(--color-muted)]'}>
            {tooShort && reason.length > 0
              ? `A sentence, please — at least ${MIN_OVERRIDE_REASON} characters.`
              : 'This is the only record of the decision, and it carries your name.'}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="override-expiry">Stop applying after (optional)</Label>
          <DatePicker
            id="override-expiry"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
          <p className="text-xs text-[var(--color-muted)]">
            Left blank it does not expire. An override granted for one mobilisation should
            usually not still be covering next year's.
          </p>
        </div>

        <p className="text-xs text-[var(--color-muted)]">
          Recorded against this {context.toLowerCase()}.
        </p>
      </div>
    </FormDialog>
  )
}
